import {
  faBars,
  faBasketShopping,
  faBell,
  faBuilding,
  faChevronRight,
  faGlobe,
  faGraduationCap,
  faPlus,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./LearnerHeader.css";

const LearnerHeader = ({
  headerRef,
  fixedHeader,
  logo,
  logoHref,
  title,
  titleOffset = 0,
  themeVars,
  renderThemeSwitcher,
  showThemeToggle,
  isOpen,
  onToggle,
  onClose,
  user,
  isLoggedIn,
  cartCount,
  notificationCount,
  profileMenu,
  t,
  lang,
  setLang,
}) => {
  const [expandedMenu, setExpandedMenu] = useState(null);
  const menuButtonRef = useRef(null);
  const firstLinkRef = useRef(null);
  const profileFlyoutRef = useRef(null);
  const isCreator = user?.role === "creator";
  const canCreate = isCreator || user?.role === "both";

  useEffect(() => {
    if (isOpen) firstLinkRef.current?.focus();
    else setExpandedMenu(null);
  }, [isOpen]);

  useEffect(() => {
    if (expandedMenu === "profile")
      profileFlyoutRef.current?.querySelector("a")?.focus();
  }, [expandedMenu]);

  const toggleSubmenu = (name) =>
    setExpandedMenu((current) => (current === name ? null : name));
  const closeMenu = () => {
    onClose();
    menuButtonRef.current?.focus();
  };
  const row = (icon, label, to, count) => (
    <Link className="learner-header-row" to={to} onClick={onClose}>
      <FontAwesomeIcon icon={icon} fixedWidth />
      <span>{label}</span>
      {count > 0 && <span className="learner-header-badge">{count}</span>}
    </Link>
  );

  return (
    <header
      ref={headerRef}
      className={`learner-header${fixedHeader ? " learner-header-fixed" : ""}`}
      style={{ ...themeVars, "--headerContentOffset": `${titleOffset}px` }}
      onKeyDown={(event) => {
        if (event.key === "Escape") closeMenu();
      }}
    >
      {logo &&
        (logoHref ? (
          <Link to={logoHref} aria-label="Home">
            <img className="learner-header-logo" src={logo} alt="Traineego" />
          </Link>
        ) : (
          <img className="learner-header-logo" src={logo} alt="Creator logo" />
        ))}
      {title && (
        <h3 className="learner-header-title" title={title}>
          {title}
        </h3>
      )}
      <div className="learner-header-controls">
        {showThemeToggle && renderThemeSwitcher()}
        <button
          ref={menuButtonRef}
          type="button"
          className="learner-header-menu-trigger"
          aria-label="Toggle navigation"
          title="Menu"
          aria-controls="learner-header-navigation"
          aria-expanded={isOpen}
          onClick={onToggle}
        >
          <FontAwesomeIcon icon={faBars} />
        </button>
      </div>
      {isOpen && (
        <nav
          className={`learner-header-menu${expandedMenu === "profile" ? " has-profile-open" : ""}`}
          id="learner-header-navigation"
          aria-label="Main navigation"
        >
          <Link
            ref={firstLinkRef}
            className="learner-header-row"
            to="/business"
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faBuilding} fixedWidth />
            <span>{t("business")}</span>
          </Link>
          {canCreate && row(faPlus, t("createCourse"), "/instructor-dashboard")}
          {isLoggedIn ? (
            <>
              {row(
                faGraduationCap,
                isCreator ? t("myTeaching") : t("myLearning"),
                isCreator ? "/my-teaching" : "/my-learning",
              )}
              {row(faBasketShopping, t("basket"), "/cart", cartCount)}
              {row(
                faBell,
                t("notifications"),
                "/notifications",
                notificationCount,
              )}
              <button
                className="learner-header-row"
                type="button"
                aria-expanded={expandedMenu === "profile"}
                aria-controls="learner-header-profile"
                onClick={() => toggleSubmenu("profile")}
              >
                <FontAwesomeIcon icon={faUser} fixedWidth />
                <span>{t("profile")}</span>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="learner-header-chevron"
                />
              </button>
            </>
          ) : (
            <>
              {row(faGraduationCap, t("teachOnTraineego"), "/auth/login")}
              {row(faUser, t("login"), "/auth/login")}
              {row(faPlus, t("signup"), "/auth/signup")}
              {row(faGraduationCap, t("membership"), "/memberships")}
            </>
          )}
          <div className="learner-header-menu-footer">
            <button
              className="learner-header-row"
              type="button"
              aria-expanded={expandedMenu === "language"}
              aria-controls="learner-header-languages"
              onClick={() => toggleSubmenu("language")}
            >
              <FontAwesomeIcon icon={faGlobe} fixedWidth />
              <span>{(lang || "en").toUpperCase()}</span>
              <FontAwesomeIcon
                icon={faChevronRight}
                rotation={expandedMenu === "language" ? 90 : undefined}
                className="learner-header-chevron"
              />
            </button>
            {expandedMenu === "language" && (
              <div
                id="learner-header-languages"
                className="learner-header-submenu"
              >
                {[
                  ["en", "English"],
                  ["es", "Espa\u00f1ol"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className="learner-header-row"
                    aria-pressed={lang === value}
                    onClick={() => {
                      setLang(value);
                      setExpandedMenu(null);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>
      )}
      {isOpen && expandedMenu === "profile" && (
        <div
          ref={profileFlyoutRef}
          id="learner-header-profile"
          className="learner-header-flyout"
          role="region"
          aria-label={t("profile")}
        >
          <button
            type="button"
            className="learner-header-row learner-header-profile-back"
            onClick={() => {
              setExpandedMenu(null);
              menuButtonRef.current?.focus();
            }}
          >
            <FontAwesomeIcon icon={faChevronRight} rotation={180} />
            <span>{t("profile")}</span>
          </button>
          <div className="learner-header-submenu">{profileMenu}</div>
        </div>
      )}
    </header>
  );
};

export default LearnerHeader;
